'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations, useLocale } from 'next-intl';
import { IoBedOutline, IoCalendarOutline, IoClose, IoFunnelOutline, IoChevronDown, IoSwapVertical, IoPersonOutline } from 'react-icons/io5';
import { LuBedDouble, LuBedSingle } from 'react-icons/lu';
import { useRouter, usePathname } from '@/i18n/navigation';
import { useBookingStore } from '@/stores/booking-store';
import { useCheckoutDraftStore } from '@/stores/checkout-draft-store';
import type { ListRoom } from '@/types/rooms';
import type { DateInterval, CalendarDay } from '@/types/calendar';
import type { RatePlanTargetSummary } from '@/types/rate-plan';
import type { BulkCalendarResponse } from '@/lib/calendar';
import { formatShortDate, getDatesBetween } from '@/lib/calendar-utils';
import { rangeBookable } from '@/lib/calendar/helpers';
import { log } from '@/lib/logger';
import { ACTIVE_VARIANT } from './design-variants';
import { MobileRoomCard } from './mobile-room-card';
import { DesktopRoomCard } from './desktop-room-card';
import { FilterCalendar } from './filter-calendar';
import {
  getBuildingLabels,
  getSortOptions,
  SidebarFilterSection,
  FilterOption,
  FilterDropdown,
  MultiFilterDropdown,
  MobileDateFilter,
  MobilePersonsFilter,
  PersonsStepper,
  extractPricesFromBulk,
} from './filter-sidebar';

// No-leapfrog range check (D23) + festive min-stay (D30) now live in
// `@/lib/calendar/helpers` as the shared, unit-tested `rangeBookable`.

// ─── Phase 2: leapfrog suggestions (doc 81 §5.4) ────────────────
// A runtime-verified clickable suggestion derived from a special-offer plan's
// featured stay window. Only built when the filter range is a true leapfrog
// (every night individually available, no single plan spans) AND the plan is
// still open across its OWN target window on ≥1 room — see the effect below.
interface PlanSuggestion {
  pynPlanId: string;
  planName: string;   // locale-resolved
  checkIn: string;    // target window check-in (ISO)
  checkOut: string;   // target window check-out (ISO)
  dateLabel: string;  // "Dec 24 – Dec 26"
}

/** Resolve a target-plan's display name for the active locale (EN fallback). */
function resolvePlanName(p: RatePlanTargetSummary, locale: string): string {
  if (locale === 'ro') return p.displayNameRo || p.displayName;
  if (locale === 'hu') return p.displayNameHu || p.displayName;
  return p.displayName;
}

// Shared empty-state for the rooms list when the date filter yields zero
// bookable rooms. Three escalating variants (doc 81 §4.7 + §5.4):
//   • suggestions present → tailored "your range crosses <plan>" + clickable
//     target-window button(s);
//   • no suggestions but a genuine leapfrog → generic "can't be booked as a
//     single stay";
//   • plain hard-unavailable → the original "all unavailable" copy.
function NoSpanEmptyState({
  compact,
  noSpanForDates,
  suggestions,
  onClear,
  onPick,
}: {
  compact: boolean;
  noSpanForDates: boolean;
  suggestions: PlanSuggestion[];
  onClear: () => void;
  onPick: (checkIn: string, checkOut: string) => void;
}) {
  const t = useTranslations('rooms');
  const hasSuggestions = suggestions.length > 0;
  const multi = suggestions.length > 1;

  const title = hasSuggestions
    ? multi
      ? t('empty.crossMultipleTitle')
      : t('empty.noSpanTitle')
    : noSpanForDates
      ? t('empty.noSpanTitle')
      : t('allUnavailableTitle');

  const body = hasSuggestions ? (
    multi ? (
      t('empty.crossMultipleBody')
    ) : (
      t.rich('empty.crossOneBody', {
        planName: suggestions[0].planName,
        b: (chunks) => <strong className="font-semibold text-gray-700">{chunks}</strong>,
      })
    )
  ) : noSpanForDates ? (
    t('empty.noSpanBody')
  ) : (
    t('allUnavailableSubtitle')
  );

  return (
    <div className={`${compact ? 'py-10' : 'py-14'} text-center`}>
      <IoCalendarOutline className={`${compact ? 'w-10 h-10 mb-3' : 'w-12 h-12 mb-4'} text-gray-300 mx-auto`} />
      <p className={`${compact ? 'text-lg' : 'text-xl'} font-semibold text-gray-700`}>{title}</p>
      <p className={`text-sm text-gray-400 ${compact ? 'mt-1' : 'mt-1.5'}`}>{body}</p>

      {hasSuggestions ? (
        <div className={`${compact ? 'mt-4' : 'mt-5'} flex flex-col items-center gap-2.5`}>
          <div className="flex flex-wrap justify-center gap-2">
            {suggestions.map((s) => (
              <button
                key={s.pynPlanId}
                onClick={() => onPick(s.checkIn, s.checkOut)}
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-[#801515] text-white text-sm font-semibold hover:bg-[#6a1111] transition-colors cursor-pointer"
              >
                {multi
                  ? t('empty.suggestionChip', { planName: s.planName, dates: s.dateLabel })
                  : t('empty.bookSuggestion', { planName: s.planName, dates: s.dateLabel })}
              </button>
            ))}
          </div>
          <button onClick={onClear} className="text-sm underline text-[#801515] cursor-pointer">
            {t('clearDates')}
          </button>
        </div>
      ) : (
        <button onClick={onClear} className={`${compact ? 'mt-4' : 'mt-5'} text-sm underline text-[#801515] cursor-pointer`}>
          {t('clearDates')}
        </button>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────

interface RoomsListViewProps {
  rooms: ListRoom[];
  roomTypes: string[];
  initialCalendarData?: BulkCalendarResponse;
  initialCheckIn?: string;
  initialCheckOut?: string;
}

export function RoomsListView({ rooms, roomTypes, initialCalendarData, initialCheckIn, initialCheckOut }: RoomsListViewProps) {
  const t = useTranslations('rooms');
  const tSelection = useTranslations('selection');
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const { items: selectedItems, setDates: setStoreDates, removeRoom } = useBookingStore();

  // ─── Filter state ────────────────────────────────────────────────
  const [filterRoomTypes, setFilterRoomTypes] = useState<string[]>([]);
  const [filterBuildings, setFilterBuildings] = useState<string[]>([]);
  const [filterBedType, setFilterBedType] = useState('');
  const [filterPersons, setFilterPersons] = useState(0);
  const [sortBy, setSortBy] = useState('default');
  const [filterCheckIn, setFilterCheckIn] = useState<string | null>(initialCheckIn ?? null);
  const [filterCheckOut, setFilterCheckOut] = useState<string | null>(initialCheckOut ?? null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // ─── URL persistence ─────────────────────────────────────────────
  const [urlInitialized, setUrlInitialized] = useState(false);

  // Read non-date URL params on mount (dates are initialized from server props via SSR)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('type')) {
      setFilterRoomTypes(params.get('type')!.split(',').map((s) => s.trim()).filter(Boolean));
    }
    if (params.get('building')) {
      setFilterBuildings(params.get('building')!.split(',').map((s) => s.trim()).filter(Boolean));
    }
    if (params.get('bed')) setFilterBedType(params.get('bed')!);
    if (params.get('persons')) {
      const n = parseInt(params.get('persons')!, 10);
      if (Number.isFinite(n) && n > 0) setFilterPersons(n);
    }
    if (params.get('sort')) setSortBy(params.get('sort')!);
    // `?plan=<pynPlanId>` — an offer page sending the guest to the rate plan
    // its copy advertises. Stored rather than kept in the URL because the
    // filter-sync effect below rebuilds the querystring from a fixed key list
    // and would drop it on the first filter change. The draft store is
    // sessionStorage-backed, so the intent survives to /checkout; CheckoutForm
    // applies it only if that plan is available, else falls back to cheapest.
    const planParam = params.get('plan');
    if (planParam) useCheckoutDraftStore.getState().setSelectedPlanId(planParam);
    setUrlInitialized(true);
  }, []);

  // Scroll past the hero to the rooms list when returning from /checkout
  // (either the native back button or the "Browse Rooms" CTA). The flag is
  // set on /checkout mount and consumed once here. Fresh visits and deep
  // links keep the default landing position.
  useEffect(() => {
    let shouldScroll = false;
    try {
      if (sessionStorage.getItem('scrollToRoomsList') === '1') {
        shouldScroll = true;
        sessionStorage.removeItem('scrollToRoomsList');
      }
    } catch { /* sessionStorage unavailable */ }
    if (shouldScroll) {
      requestAnimationFrame(() => {
        document.getElementById('rooms-list')?.scrollIntoView({ block: 'start' });
      });
    }
  }, []);

  // Seed booking store with URL dates on mount
  useEffect(() => {
    if (initialCheckIn && initialCheckOut) {
      setStoreDates(initialCheckIn, initialCheckOut);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filter state → URL via the Next.js router so the entry recorded
  // in history matches the current filters. Using window.history.replaceState
  // alone updates the URL bar but leaves Next.js's internal router state
  // pointed at the bare /rooms URL — so a router.push('/checkout') and then
  // a native Back lands on the unfiltered page. router.replace keeps both
  // in sync; { scroll: false } prevents jumping the page.
  useEffect(() => {
    if (!urlInitialized) return;
    const params = new URLSearchParams();
    const sync = (key: string, value: string | null | undefined) => {
      if (value) params.set(key, value);
    };
    sync('checkIn', filterCheckIn);
    sync('checkOut', filterCheckOut);
    sync('type', filterRoomTypes.length > 0 ? filterRoomTypes.join(',') : null);
    sync('building', filterBuildings.length > 0 ? filterBuildings.join(',') : null);
    sync('bed', filterBedType || null);
    sync('persons', filterPersons > 0 ? String(filterPersons) : null);
    sync('sort', sortBy !== 'default' ? sortBy : null);
    const qs = params.toString();
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const target = `${pathname}${qs ? `?${qs}` : ''}${hash}`;
    // Stash the current (locale-stripped) rooms URL so the checkout page's
    // explicit "Browse Rooms" button can return the user to the same
    // filter context they left.
    try {
      sessionStorage.setItem('lastRoomsUrl', `${pathname}${qs ? `?${qs}` : ''}`);
    } catch { /* sessionStorage unavailable */ }
    // Skip the router.replace when URL already matches state. This is the
    // common case on first run after mount (we just hydrated state from
    // the URL). A no-op replace still triggers an RSC fetch whose late
    // arrival can clobber a subsequent native Forward navigation, briefly
    // loading the forward page (e.g. /checkout) before yanking the user
    // back to /rooms.
    const currentQs = typeof window !== 'undefined'
      ? window.location.search.replace(/^\?/, '')
      : '';
    if (currentQs === qs) return;
    router.replace(target, { scroll: false });
  }, [urlInitialized, filterCheckIn, filterCheckOut, filterRoomTypes, filterBuildings, filterBedType, filterPersons, sortBy, pathname, router]);

  // ─── Shared calendar state for synced month navigation ─────────
  const now = new Date();
  const initCalendarYear = initialCheckIn ? parseInt(initialCheckIn.substring(0, 4)) : now.getFullYear();
  const initCalendarMonth = initialCheckIn ? parseInt(initialCheckIn.substring(5, 7)) : now.getMonth() + 1;
  const [calendarYear, setCalendarYear] = useState(initCalendarYear);
  const [calendarMonth, setCalendarMonth] = useState(initCalendarMonth);
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);

  // Client-side month cache: "year-month" → room data
  type MonthData = Record<string, { days: CalendarDay[]; currency: string; minPrice: number | null }>;
  const monthCache = useRef<Map<string, MonthData>>(new Map());
  // Bumped on every monthCache write so `globalMinStay` (a ref-derived memo)
  // recomputes when new month data lands.
  const [monthCacheVersion, setMonthCacheVersion] = useState(0);

  // Featured-window summary for special-offer plans (doc 81 §5.5), seeded from
  // SSR and refreshed from any bulk fetch that carries it. Month-independent,
  // so a ref (no re-render) — the suggestion builder reads it in the effect.
  const ratePlanTargetsRef = useRef<RatePlanTargetSummary[]>(initialCalendarData?.ratePlans ?? []);

  // Calendar data per room: slug → { days, currency, minPrice }
  const [calendarData, setCalendarData] = useState<MonthData>(() => {
    if (!initialCalendarData?.rooms) return {};
    const result: MonthData = {};
    for (const [slug, roomResult] of Object.entries(initialCalendarData.rooms)) {
      if (roomResult.success && roomResult.data) {
        result[slug] = {
          days: roomResult.data.days,
          currency: roomResult.data.currency,
          minPrice: roomResult.data.minPrice ?? null,
        };
      }
    }
    // Seed cache with SSR data — use the month that was actually fetched
    monthCache.current.set(`${initCalendarYear}-${initCalendarMonth}`, result);
    return result;
  });

  // SSR-derived prices for initial render (no loading spinners)
  const ssrPrices = useMemo(() => extractPricesFromBulk(calendarData), [calendarData]);

  // Global per-date min-stay (D30, doc 81 §12.4 D): `min` across all rooms of
  // each day's min-stay, over every loaded month. Forces a festive N-night
  // minimum on the sidebar filter's first click only when at least one room
  // requires it that day. Absent dates default to 1 in FilterCalendar.
  const globalMinStay = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    for (const monthData of monthCache.current.values()) {
      for (const roomData of Object.values(monthData)) {
        for (const day of roomData.days) {
          const prev = out[day.date];
          out[day.date] = prev === undefined ? day.minStay : Math.min(prev, day.minStay);
        }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthCacheVersion]);

  // Handle synced month navigation from any card's calendar
  const handleMonthChange = useCallback((newYear: number, newMonth: number) => {
    setCalendarYear(newYear);
    setCalendarMonth(newMonth);

    // Check client cache first — instant on back-navigation
    const cacheKey = `${newYear}-${newMonth}`;
    const cached = monthCache.current.get(cacheKey);
    if (cached) {
      setCalendarData(cached);
      return;
    }

    setIsLoadingCalendar(true);

    fetch(`/api/calendar?year=${newYear}&month=${newMonth}`)
      .then(res => res.json())
      .then((data: BulkCalendarResponse) => {
        if (data.success && data.rooms) {
          const result: MonthData = {};
          for (const [slug, roomResult] of Object.entries(data.rooms)) {
            if (roomResult.success && roomResult.data) {
              result[slug] = {
                days: roomResult.data.days,
                currency: roomResult.data.currency,
                minPrice: roomResult.data.minPrice ?? null,
              };
            }
          }
          monthCache.current.set(cacheKey, result);
          setMonthCacheVersion((v) => v + 1);
          if (data.ratePlans && data.ratePlans.length > 0) {
            ratePlanTargetsRef.current = data.ratePlans;
          }
          setCalendarData(result);
        }
      })
      .catch(err => log.error('rooms_list.bulk_calendar.fetch_failed', { year: newYear, month: newMonth, err }))
      .finally(() => setIsLoadingCalendar(false));
  }, []);

  // Filter interval for FilterCalendar
  const filterInterval: DateInterval = { checkIn: filterCheckIn, checkOut: filterCheckOut };
  // Track a version counter so DesktopRoomCard can detect external filter changes
  const [filterIntervalVersion, setFilterIntervalVersion] = useState(0);
  // Date conflict modal state (shown when date change makes selected rooms unavailable)
  const [dateConflict, setDateConflict] = useState<{ unavailableSlugs: string[]; pendingDates: DateInterval } | null>(null);
  const prevFilterDates = useRef<{ checkIn: string | null; checkOut: string | null }>({ checkIn: null, checkOut: null });

  const handleFilterIntervalChange = useCallback((iv: DateInterval) => {
    prevFilterDates.current = { checkIn: filterCheckIn, checkOut: filterCheckOut };
    setFilterCheckIn(iv.checkIn);
    setFilterCheckOut(iv.checkOut);
    setFilterIntervalVersion(v => v + 1);

    // Sync to booking store when dates are set
    if (iv.checkIn && iv.checkOut && selectedItems.length > 0) {
      setStoreDates(iv.checkIn, iv.checkOut);
    }

    // Navigate card calendars to the check-in month
    if (iv.checkIn) {
      const y = parseInt(iv.checkIn.substring(0, 4));
      const m = parseInt(iv.checkIn.substring(5, 7));
      handleMonthChange(y, m);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleMonthChange, filterCheckIn, filterCheckOut, selectedItems.length]);

  // ─── Date filter: availability checking ────────────────────────
  // Compute initial unavailable rooms from SSR data to avoid flash on first render.
  // SSR ships only the check-in month, so a range crossing into another month
  // is partially covered — stay lenient there (defer to the effect below) and
  // only apply the full no-leapfrog rangeBookable check when the whole range
  // is within the fetched month. A hard-unavailable night we CAN see still
  // marks the room unavailable immediately.
  const [unavailableForDates, setUnavailableForDates] = useState<Set<string>>(() => {
    if (!initialCheckIn || !initialCheckOut || !initialCalendarData?.rooms) return new Set();
    const nights = getDatesBetween(initialCheckIn, initialCheckOut);
    const unavailable = new Set<string>();
    for (const room of rooms) {
      const roomResult = initialCalendarData.rooms[room.slug];
      if (!roomResult?.success || !roomResult.data) continue;
      const dayMap = new Map(roomResult.data.days.map((d) => [d.date, d]));
      const coveredNights = nights.filter((d) => dayMap.has(d));
      if (coveredNights.length === 0) continue; // no data this month → defer to effect
      const anyHardUnavailable = coveredNights.some((d) => !dayMap.get(d)!.available);
      const fullyCovered = coveredNights.length === nights.length;
      if (anyHardUnavailable || (fullyCovered && !rangeBookable(dayMap, nights))) {
        unavailable.add(room.slug);
      }
    }
    return unavailable;
  });
  // True when the date filter yields zero bookable rooms specifically because
  // some room's nights cross rate-plan windows that don't span the full range
  // (D23 leapfrog), as opposed to plain "nothing available". Drives the
  // tailored rooms-list empty-state (doc 81 §4.7). Phase 2 (§5.4) refines this
  // into clickable suggestions.
  const [noSpanForDates, setNoSpanForDates] = useState(false);
  // Runtime-verified clickable suggestions for the leapfrog empty-state
  // (doc 81 §5.4). Up to 2, ranked by overlap with the user's filter range.
  const [suggestions, setSuggestions] = useState<PlanSuggestion[]>([]);
  const hasFilterDates = filterCheckIn !== null && filterCheckOut !== null;

  useEffect(() => {
    if (!filterCheckIn || !filterCheckOut) {
      setUnavailableForDates(new Set());
      setNoSpanForDates(false);
      setSuggestions([]);
      return;
    }

    const checkAvailability = async () => {
      // Fetch the bulk calendar for any month touched by `dateList` that isn't
      // already cached. Also captures the featured-window summary (§5.5) when a
      // response carries it. Shared by the filter-range pass and the §5.4
      // target-window verification.
      const ensureMonths = async (dateList: string[]) => {
        const missing: { year: number; month: number }[] = [];
        const seen = new Set<string>();
        for (const date of dateList) {
          const y = parseInt(date.substring(0, 4));
          const m = parseInt(date.substring(5, 7));
          const key = `${y}-${m}`;
          if (seen.has(key) || monthCache.current.has(key)) continue;
          seen.add(key);
          missing.push({ year: y, month: m });
        }
        if (missing.length === 0) return;
        await Promise.all(missing.map(async ({ year, month }) => {
          try {
            const res = await fetch(`/api/calendar?year=${year}&month=${month}`);
            const data: BulkCalendarResponse = await res.json();
            if (data.success && data.rooms) {
              const result: MonthData = {};
              for (const [slug, roomResult] of Object.entries(data.rooms)) {
                if (roomResult.success && roomResult.data) {
                  result[slug] = {
                    days: roomResult.data.days,
                    currency: roomResult.data.currency,
                    minPrice: roomResult.data.minPrice ?? null,
                  };
                }
              }
              monthCache.current.set(`${year}-${month}`, result);
              setMonthCacheVersion((v) => v + 1);
              if (data.ratePlans && data.ratePlans.length > 0) {
                ratePlanTargetsRef.current = data.ratePlans;
              }
            }
          } catch (err) {
            log.error('rooms_list.calendar.fetch_failed', { year, month, err });
          }
        }));
      };

      const datesInRange = getDatesBetween(filterCheckIn, filterCheckOut);
      await ensureMonths(datesInRange);

      // Look up a single CalendarDay from the month cache.
      const dayFor = (slug: string, date: string): CalendarDay | undefined => {
        const y = parseInt(date.substring(0, 4));
        const m = parseInt(date.substring(5, 7));
        return monthCache.current.get(`${y}-${m}`)?.[slug]?.days?.find((d) => d.date === date);
      };

      // Check each room's availability for the full date range using the
      // no-leapfrog rangeBookable rule (D23): a room is bookable only if a
      // single rate plan is open across every night. A room whose nights are
      // each individually available but share no common plan is a "no-span"
      // (leapfrog) case — surfaced via the tailored empty-state + a sanity log.
      const unavailable = new Set<string>();
      let anyNoSpan = false;
      for (const room of rooms) {
        const dayMap = new Map<string, CalendarDay>();
        for (const date of datesInRange) {
          const dayData = dayFor(room.slug, date);
          if (dayData) dayMap.set(date, dayData);
        }
        if (!rangeBookable(dayMap, datesInRange)) {
          unavailable.add(room.slug);
          // Distinguish leapfrog (every night individually available, but no
          // single plan spans) from plain hard-unavailable.
          const allNightsIndividuallyAvailable = datesInRange.every(d => dayMap.get(d)?.available);
          if (allNightsIndividuallyAvailable) {
            anyNoSpan = true;
            log.info('rooms_list.rate_plan_restriction.no_span', { room: room.slug, checkIn: filterCheckIn, checkOut: filterCheckOut });
          }
        }
      }
      setUnavailableForDates(unavailable);
      setNoSpanForDates(anyNoSpan);

      // ── Phase 2: build clickable suggestions for a true leapfrog (§5.4) ──
      // Only when we have a genuine leapfrog and at least one plan has a
      // featured window configured. Hard-unavailable ranges get no suggestion.
      const targets = ratePlanTargetsRef.current;
      if (!anyNoSpan || targets.length === 0) {
        setSuggestions([]);
        return;
      }

      // 1. Plan IDs observed open on ≥1 available night in the filter range,
      //    across all rooms — these are the offers the range "crosses".
      const observed = new Set<string>();
      for (const room of rooms) {
        for (const date of datesInRange) {
          const d = dayFor(room.slug, date);
          if (d?.available) for (const id of d.openPlanIds) observed.add(id);
        }
      }

      // 2. Candidate target plans = configured-window plans observed in range.
      const candidates = targets.filter((p) => observed.has(p.pynPlanId));
      if (candidates.length === 0) {
        setSuggestions([]);
        return;
      }

      // 3. Load the target-window months, then runtime-verify each plan is
      //    still open across its OWN window on ≥1 room. A target that drifted
      //    out of PYN's open window (scenario 7.4) fails here and is dropped.
      await ensureMonths(candidates.flatMap((p) => getDatesBetween(p.targetCheckin, p.targetCheckout)));
      const filterNights = new Set(datesInRange);
      const verified = candidates
        .map((p) => {
          const nights = getDatesBetween(p.targetCheckin, p.targetCheckout);
          const bookable = nights.length > 0 && rooms.some((room) =>
            nights.every((date) => {
              const d = dayFor(room.slug, date);
              return !!d && d.available && d.openPlanIds.includes(p.pynPlanId);
            })
          );
          // 4. Rank by overlap (nights shared with the user's filter range).
          const overlap = nights.filter((d) => filterNights.has(d)).length;
          return { p, bookable, overlap };
        })
        .filter((x) => x.bookable)
        .sort((a, b) => b.overlap - a.overlap)
        .slice(0, 2)
        .map(({ p }) => ({
          pynPlanId: p.pynPlanId,
          planName: resolvePlanName(p, locale),
          checkIn: p.targetCheckin,
          checkOut: p.targetCheckout,
          dateLabel: `${formatShortDate(p.targetCheckin)} – ${formatShortDate(p.targetCheckout)}`,
        }));

      setSuggestions(verified);
    };

    checkAvailability();
  }, [filterCheckIn, filterCheckOut, rooms, locale]);

  // Detect when selected rooms become unavailable after a date change
  useEffect(() => {
    if (selectedItems.length === 0 || unavailableForDates.size === 0) return;
    const conflicting = selectedItems
      .filter((item) => unavailableForDates.has(item.roomSlug))
      .map((item) => item.roomSlug);
    if (conflicting.length > 0 && filterCheckIn && filterCheckOut) {
      setDateConflict({
        unavailableSlugs: conflicting,
        pendingDates: { checkIn: filterCheckIn, checkOut: filterCheckOut },
      });
    }
  }, [unavailableForDates, selectedItems, filterCheckIn, filterCheckOut]);

  const handleConflictRemoveUnavailable = useCallback(() => {
    if (!dateConflict) return;
    for (const slug of dateConflict.unavailableSlugs) {
      removeRoom(slug);
    }
    setDateConflict(null);
  }, [dateConflict, removeRoom]);

  const handleConflictKeepDates = useCallback(() => {
    if (!dateConflict) return;
    // Revert to previous dates
    const prev = prevFilterDates.current;
    if (prev.checkIn && prev.checkOut) {
      setFilterCheckIn(prev.checkIn);
      setFilterCheckOut(prev.checkOut);
      setFilterIntervalVersion(v => v + 1);
      if (selectedItems.length > 0) {
        setStoreDates(prev.checkIn, prev.checkOut);
      }
    }
    setDateConflict(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateConflict, selectedItems.length]);

  // Derive available buildings from rooms data (preferred display order: Daniel, Bethlen, Crafts)
  const buildings = useMemo(() => {
    const set = new Set(rooms.map((r) => r.building).filter(Boolean) as string[]);
    const order = ['daniel-castle', 'bethlen-castle', 'crafts-house'];
    const known = order.filter((b) => set.has(b));
    const unknown = Array.from(set).filter((b) => !order.includes(b)).sort();
    return [...known, ...unknown];
  }, [rooms]);

  // Fixed bed-type filter options. "double" matches everything (every room has
  // at least a double-equivalent main bed); "twin" matches rooms whose bed is
  // a twin or can be split into twins; "extra-bed" requires the count, not
  // the boolean flag.
  const ExtraBedIcon = (
    <span className="relative inline-flex">
      <LuBedSingle className="w-4 h-4" />
      <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-current flex items-center justify-center leading-none">
        <svg viewBox="0 0 8 8" className="w-2 h-2 text-white"><path d="M4 1v7M1 4h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
      </span>
    </span>
  );
  const TwinIcon = (
    <span className="inline-flex items-center -space-x-0.5">
      <LuBedSingle className="w-3.5 h-3.5" />
      <LuBedSingle className="w-3.5 h-3.5" />
    </span>
  );
  const bedTypeOptions = [
    { value: 'double', label: t('bedTypes.double'), icon: <LuBedDouble className="w-4 h-4" /> },
    { value: 'twin', label: t('bedTypes.twin'), icon: TwinIcon },
    { value: 'extra-bed', label: t('bedTypes.extraBed'), icon: ExtraBedIcon },
  ];

  const matchesBedTypeFilter = (room: ListRoom, value: string): boolean => {
    if (value === 'double') return true;
    if (value === 'twin') return room.bed_type === 'twin' || room.can_split_to_twins;
    if (value === 'extra-bed') return (room.extra_beds || 0) > 0;
    return true;
  };

  // Total persons capacity = base double bed (2) + all extra beds (adults or
  // children may use them — children-only beds count toward the total since
  // a child still occupies a person-slot).
  const getRoomMaxAdults = (room: ListRoom): number => {
    return 2 + (room.extra_beds || 0);
  };

  const maxPersonsAvailable = useMemo(() => {
    if (rooms.length === 0) return 4;
    return Math.max(...rooms.map(getRoomMaxAdults));
  }, [rooms]);

  // Filter + sort rooms, separating available from unavailable
  const filteredResult = useMemo(() => {
    const available: ListRoom[] = [];
    const unavailable: ListRoom[] = [];

    for (const room of rooms) {
      if (filterRoomTypes.length > 0 && (!room.room_type_name || !filterRoomTypes.includes(room.room_type_name))) continue;
      if (filterBuildings.length > 0 && (!room.building || !filterBuildings.includes(room.building))) continue;
      if (filterBedType && !matchesBedTypeFilter(room, filterBedType)) continue;
      if (filterPersons > 0 && getRoomMaxAdults(room) < filterPersons) continue;

      if (hasFilterDates && unavailableForDates.has(room.slug)) {
        unavailable.push(room);
      } else {
        available.push(room);
      }
    }

    const sortFn = (a: ListRoom, b: ListRoom) => {
      switch (sortBy) {
        case 'price-asc':
        case 'price-desc': {
          const pa = ssrPrices[a.slug]?.price ?? null;
          const pb = ssrPrices[b.slug]?.price ?? null;
          if (pa === null && pb === null) return 0;
          if (pa === null) return 1;  // no price → end
          if (pb === null) return -1;
          return sortBy === 'price-asc' ? pa - pb : pb - pa;
        }
        case 'number-asc': return (a.number || '').localeCompare(b.number || '', undefined, { numeric: true });
        case 'number-desc': return (b.number || '').localeCompare(a.number || '', undefined, { numeric: true });
        case 'size-asc': return (a.size || 0) - (b.size || 0);
        case 'size-desc': return (b.size || 0) - (a.size || 0);
        case 'capacity-desc': return (b.extra_beds || 0) - (a.extra_beds || 0);
        case 'capacity-asc': return (a.extra_beds || 0) - (b.extra_beds || 0);
        default: return 0;
      }
    };

    if (sortBy !== 'default') {
      available.sort(sortFn);
      unavailable.sort(sortFn);
    }

    return { available, unavailable };
  }, [rooms, filterRoomTypes, filterBuildings, filterBedType, filterPersons, sortBy, hasFilterDates, unavailableForDates, ssrPrices]);

  const activeFilterCount = (filterRoomTypes.length > 0 ? 1 : 0) + (filterBuildings.length > 0 ? 1 : 0) + (filterBedType ? 1 : 0) + (filterPersons > 0 ? 1 : 0);

  const clearAllFilters = useCallback(() => {
    setFilterRoomTypes([]);
    setFilterBuildings([]);
    setFilterBedType('');
    setFilterPersons(0);
    setSortBy('default');
    setFilterCheckIn(null);
    setFilterCheckOut(null);
  }, []);

  const dateParams = hasFilterDates ? `?checkIn=${filterCheckIn}&checkOut=${filterCheckOut}` : '';

  // Scroll to top of room list when filters change — but only if the user
  // has already scrolled into the list area (sticky sidebar is locked at
  // the top). If they're above the list, the sidebar is still in normal
  // flow and an auto-scroll would yank the calendar/filters from under
  // their cursor mid-click.
  const filterRoomTypesKey = filterRoomTypes.join(',');
  const filterBuildingsKey = filterBuildings.join(',');
  const prevFiltersRef = useRef({ filterRoomTypesKey, filterBuildingsKey, filterBedType, filterPersons, sortBy, filterCheckIn, filterCheckOut });
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const changed = prev.filterRoomTypesKey !== filterRoomTypesKey || prev.filterBuildingsKey !== filterBuildingsKey
      || prev.filterBedType !== filterBedType || prev.filterPersons !== filterPersons || prev.sortBy !== sortBy
      || prev.filterCheckIn !== filterCheckIn || prev.filterCheckOut !== filterCheckOut;
    prevFiltersRef.current = { filterRoomTypesKey, filterBuildingsKey, filterBedType, filterPersons, sortBy, filterCheckIn, filterCheckOut };
    if (changed && urlInitialized) {
      const el = document.getElementById('rooms-list');
      if (el) {
        const rect = el.getBoundingClientRect();
        // rect.top <= 65 means the list has already scrolled into the
        // header zone; user is reading rooms and the sidebar is sticky.
        // Only then is a scroll-to-top safe (and useful).
        if (rect.top <= 65) {
          const top = rect.top + window.scrollY - 65;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }
    }
  }, [filterRoomTypesKey, filterBuildingsKey, filterBedType, filterPersons, sortBy, filterCheckIn, filterCheckOut, urlInitialized]);

  const v = ACTIVE_VARIANT;
  const buildingLabels = getBuildingLabels(t);
  const sortOptions = getSortOptions(t);

  // ─── Desktop Sidebar ─────────────────────────────────────────
  const desktopSidebar = (
    <aside className="w-[216px] shrink-0 hidden min-[800px]:block sticky top-[5.5rem] self-start max-h-[calc(100vh-6.5rem)] overflow-y-auto">
      <div className="space-y-1 bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-1.5">
        {activeFilterCount > 0 && (
          <div className="flex justify-end mb-1">
            <button
              onClick={clearAllFilters}
              className="text-xs text-[#801515] hover:underline"
            >
              {t('clearAll')}
            </button>
          </div>
        )}

        {/* Date filter calendar — always visible at top */}
        <div className="border-b border-gray-200 pb-3">
          <div className="flex items-center justify-between py-2">
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <IoCalendarOutline className="w-4 h-4 text-gray-400" />
              {t('dates')}
            </span>
            {hasFilterDates && (
              <button
                onClick={() => handleFilterIntervalChange({ checkIn: null, checkOut: null })}
                className="text-xs text-[#801515] hover:underline cursor-pointer"
              >
                {t('clearDates')}
              </button>
            )}
          </div>
          {hasFilterDates && (
            <div className="mb-2 bg-[#801515]/5 rounded text-xs text-[#801515] font-medium flex items-stretch overflow-hidden">
              <span className="flex-1 text-center py-1 px-2">{formatShortDate(filterCheckIn!)} &ndash; {formatShortDate(filterCheckOut!)}</span>
              <button
                onClick={() => handleFilterIntervalChange({ checkIn: null, checkOut: null })}
                className="w-6 shrink-0 flex items-center justify-center rounded-r bg-[#801515]/10 hover:bg-[#801515]/20 transition-colors cursor-pointer"
              >
                <IoClose className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <FilterCalendar interval={filterInterval} onIntervalChange={handleFilterIntervalChange} minStayByDate={globalMinStay} />
        </div>

        {/* Persons */}
        <SidebarFilterSection
          label={t('filterPersons')}
          icon={<IoPersonOutline className="w-4 h-4 text-gray-400" />}
        >
          <PersonsStepper
            value={filterPersons}
            onChange={setFilterPersons}
            max={maxPersonsAvailable}
            anyLabel={t('personsAny')}
            unitSingular={t('personsUnitSingular')}
            unitPlural={t('personsUnitPlural')}
            maxLabel={t('personsMaxReached')}
          />
        </SidebarFilterSection>

        {/* Room Type */}
        <SidebarFilterSection
          label={t('filterRoomType')}
          icon={<IoBedOutline className="w-4 h-4 text-gray-400" />}
        >
          <div className="space-y-0.5">
            {roomTypes.map((rt) => (
              <FilterOption
                key={rt}
                label={rt}
                selected={filterRoomTypes.includes(rt)}
                onClick={() => setFilterRoomTypes((prev) => prev.includes(rt) ? prev.filter((x) => x !== rt) : [...prev, rt])}
              />
            ))}
          </div>
        </SidebarFilterSection>

        {/* Building */}
        {buildings.length > 1 && (
          <SidebarFilterSection
            label={t('filterBuilding')}
            icon={<svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
          >
            <div className="space-y-0.5">
              {buildings.map((b) => (
                <FilterOption
                  key={b}
                  label={buildingLabels[b] || b}
                  selected={filterBuildings.includes(b)}
                  onClick={() => setFilterBuildings((prev) => prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b])}
                />
              ))}
            </div>
          </SidebarFilterSection>
        )}

        {/* Bed Type */}
        <SidebarFilterSection
          label={t('filterBedType')}
          icon={<IoBedOutline className="w-4 h-4 text-gray-400" />}
        >
          <div className="space-y-0.5">
            {bedTypeOptions.map((opt) => (
              <FilterOption
                key={opt.value}
                label={opt.label}
                icon={opt.icon}
                selected={filterBedType === opt.value}
                onClick={() => setFilterBedType(filterBedType === opt.value ? '' : opt.value)}
              />
            ))}
          </div>
        </SidebarFilterSection>

        {/* Sort */}
        <SidebarFilterSection
          label={t('filterSort')}
          icon={<IoSwapVertical className="w-4 h-4 text-gray-400" />}
        >
          <div className="space-y-0.5">
            {sortOptions.map((opt) => (
              <FilterOption
                key={opt.value}
                label={opt.label}
                selected={sortBy === opt.value}
                onClick={() => setSortBy(opt.value)}
              />
            ))}
          </div>
        </SidebarFilterSection>
      </div>
    </aside>
  );

  return (
    <div id="rooms-list" className="min-h-screen bg-white transition-colors duration-500">

      {/* Responsive calendar styles for two-column card layout */}
      <style>{`
        @media (min-width: 800px) and (max-width: 1699px) {
          .mini-calendar-area { font-size: 0.8125rem; font-weight: 500; }
          .mini-calendar-area .text-base { font-size: 0.8125rem !important; font-weight: 500; }
          .mini-calendar-area > div { width: 240px !important; }
        }
      `}</style>

      {/* ━━━ MOBILE: Sticky filter bar — pr clears the fixed hamburger corner ━━━ */}
      <div className={`min-[800px]:hidden sticky top-0 z-40 px-4 pr-[68px] min-h-[76px] flex flex-col justify-center py-2 ${v.headerBg} transition-all duration-500`}>
        <div className="flex flex-wrap items-center gap-2">
          <MobileDateFilter variant={v} interval={filterInterval} onIntervalChange={handleFilterIntervalChange} minStayByDate={globalMinStay} />
          <button
            onClick={() => setShowMobileFilters((s) => !s)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
              activeFilterCount > 0
                ? `${v.mobileCtaBg} ${v.mobileCtaText} border-transparent`
                : `${v.mobileCardBg} ${v.mobileCardBorder} ${v.headerText}`
            }`}
          >
            <IoFunnelOutline className="w-4 h-4" />
            <span>{t('filters')}</span>
            {activeFilterCount > 0 && (
              <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className={`text-xs underline underline-offset-2 ${v.headerMuted}`}
            >
              {t('clearAll')}
            </button>
          )}
        </div>

        {/* Mobile filter panel — slides down */}
        <div className={`transition-all duration-300 ${showMobileFilters ? 'max-h-[200px] opacity-100 mt-2.5 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <div className="flex flex-wrap items-center gap-2 pb-1">
            <MultiFilterDropdown
              label={t('filterRoomType')}
              icon={<IoBedOutline className="w-4 h-4" />}
              options={roomTypes.map((rt) => ({ value: rt, label: rt }))}
              selected={filterRoomTypes}
              onToggle={(val) => setFilterRoomTypes((prev) => prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val])}
              onClear={() => setFilterRoomTypes([])}
              variant={v}
            />
            {buildings.length > 1 && (
              <MultiFilterDropdown
                label={t('filterBuilding')}
                icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
                options={buildings.map((b) => ({ value: b, label: buildingLabels[b] || b }))}
                selected={filterBuildings}
                onToggle={(val) => setFilterBuildings((prev) => prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val])}
                onClear={() => setFilterBuildings([])}
                variant={v}
              />
            )}
            <FilterDropdown
              label={t('filterBedType')}
              icon={<IoBedOutline className="w-4 h-4" />}
              options={bedTypeOptions}
              selected={filterBedType}
              onSelect={setFilterBedType}
              variant={v}
            />
            <MobilePersonsFilter
              value={filterPersons}
              onChange={setFilterPersons}
              max={maxPersonsAvailable}
              variant={v}
              label={t('filterPersons')}
              anyLabel={t('personsAny')}
              unitSingular={t('personsUnitSingular')}
              unitPlural={t('personsUnitPlural')}
              maxLabel={t('personsMaxReached')}
            />
            <FilterDropdown
              label={t('filterSort')}
              icon={<IoSwapVertical className="w-4 h-4" />}
              options={sortOptions}
              selected={sortBy === 'default' ? '' : sortBy}
              onSelect={(val) => setSortBy(val || 'default')}
              variant={v}
            />
          </div>
          <button
            onClick={() => setShowMobileFilters(false)}
            className="w-full flex justify-center pt-1 pb-0.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <IoChevronDown className="w-5 h-5 rotate-180" />
          </button>
        </div>
      </div>

      {/* ━━━ MOBILE: Room cards ━━━ */}
      <main className="min-[800px]:hidden max-w-xl mx-auto px-4 py-5">
        {filteredResult.available.length === 0 && filteredResult.unavailable.length === 0 ? (
          <div className="py-20 text-center">
            <p className={`text-lg ${v.headerMuted}`}>{t('noRoomsMatch')}</p>
            <button onClick={clearAllFilters} className={`mt-3 text-sm underline ${v.headerAccent}`}>
              {t('clearFilters')}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {filteredResult.available.map((room) => (
              <MobileRoomCard
                key={room.slug}
                room={room}
                variant={v}
                ssrPrice={ssrPrices[room.slug]?.price}
                ssrCurrency={ssrPrices[room.slug]?.currency}
                dateParams={dateParams}
                filterInterval={filterInterval}
              />
            ))}
            {filteredResult.unavailable.length > 0 && (
              <>
                {filteredResult.available.length === 0 ? (
                  <NoSpanEmptyState
                    compact
                    noSpanForDates={noSpanForDates}
                    suggestions={suggestions}
                    onClear={() => handleFilterIntervalChange({ checkIn: null, checkOut: null })}
                    onPick={(ci, co) => handleFilterIntervalChange({ checkIn: ci, checkOut: co })}
                  />
                ) : (
                  <div className="flex items-center gap-3 py-4">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{t('notAvailableForDates')}</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                )}
                {filteredResult.unavailable.map((room) => (
                  <div key={room.slug}>
                    <MobileRoomCard
                      room={room}
                      variant={v}
                      ssrPrice={ssrPrices[room.slug]?.price}
                      ssrCurrency={ssrPrices[room.slug]?.currency}
                      isDateUnavailable
                      dateParams={dateParams}
                      filterInterval={filterInterval}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </main>

      {/* ━━━ DESKTOP: Sidebar + Cards layout ━━━ */}
      <div className="hidden min-[800px]:flex mx-auto px-6 xl:px-10 py-8 gap-6 xl:gap-8">
        {desktopSidebar}

        {/* Cards area — 2-column grid */}
        <main className="flex-1 min-w-0">
          {filteredResult.available.length === 0 && filteredResult.unavailable.length === 0 ? (
            <div className="py-20 text-center">
              <p className={`text-lg ${v.headerMuted}`}>{t('noRoomsMatch')}</p>
              <button onClick={clearAllFilters} className={`mt-3 text-sm underline ${v.headerAccent}`}>
                {t('clearFilters')}
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 min-[1400px]:grid-cols-2 gap-8 min-[1400px]:gap-[2vw]">
                {filteredResult.available.map((room) => (
                  <DesktopRoomCard
                    key={room.slug}
                    room={room}
                    variant={v}
                    ssrPrice={ssrPrices[room.slug]?.price}
                    ssrCurrency={ssrPrices[room.slug]?.currency}
                    calendarDays={calendarData[room.slug]?.days}
                    calendarYear={calendarYear}
                    calendarMonth={calendarMonth}
                    onMonthChange={handleMonthChange}
                    isLoadingCalendar={isLoadingCalendar}
                    filterInterval={filterInterval}
                    filterIntervalVersion={filterIntervalVersion}
                    onSetFilterDates={handleFilterIntervalChange}
                  />
                ))}
              </div>
              {filteredResult.unavailable.length > 0 && (
                <>
                  {filteredResult.available.length === 0 ? (
                    <NoSpanEmptyState
                      compact={false}
                      noSpanForDates={noSpanForDates}
                      suggestions={suggestions}
                      onClear={() => handleFilterIntervalChange({ checkIn: null, checkOut: null })}
                      onPick={(ci, co) => handleFilterIntervalChange({ checkIn: ci, checkOut: co })}
                    />
                  ) : (
                    <div className="flex items-center gap-4 py-6 mt-8">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{t('notAvailableForDates')}</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                  )}
                  <div className="grid grid-cols-1 min-[1400px]:grid-cols-2 gap-8 min-[1400px]:gap-[2vw]">
                    {filteredResult.unavailable.map((room) => (
                      <DesktopRoomCard
                        key={room.slug}
                        room={room}
                        variant={v}
                        ssrPrice={ssrPrices[room.slug]?.price}
                        ssrCurrency={ssrPrices[room.slug]?.currency}
                        calendarDays={calendarData[room.slug]?.days}
                        calendarYear={calendarYear}
                        calendarMonth={calendarMonth}
                        onMonthChange={handleMonthChange}
                        isLoadingCalendar={isLoadingCalendar}
                        filterInterval={filterInterval}
                        filterIntervalVersion={filterIntervalVersion}
                        isDateUnavailable
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </main>
      </div>

      <div className="h-12" />

      {/* Date conflict modal */}
      {dateConflict && createPortal(
        <>
          <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm" onClick={() => setDateConflict(null)} />
          <div className="fixed inset-0 z-[71] flex items-center justify-center p-4 pointer-events-none">
            <div className="pointer-events-auto bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-[#4e3529] mb-2">
                {dateConflict.unavailableSlugs.length === selectedItems.length
                  ? tSelection('dateConflictAllTitle')
                  : tSelection('dateConflictTitle')}
              </h3>
              <p className="text-sm text-[#4e3529]/65 mb-4">
                {dateConflict.unavailableSlugs.length === selectedItems.length
                  ? tSelection('dateConflictAllMessage')
                  : tSelection('dateConflictMessage')}
              </p>
              <div className="space-y-2 mb-5">
                {dateConflict.unavailableSlugs.map((slug) => {
                  const item = selectedItems.find((i) => i.roomSlug === slug);
                  return item ? (
                    <div key={slug} className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      <IoBedOutline className="w-4 h-4 shrink-0" />
                      {item.roomName}
                    </div>
                  ) : null;
                })}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleConflictKeepDates}
                  className="flex-1 py-2.5 px-4 text-sm font-semibold border border-[#4e3529]/20 text-[#4e3529] rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  {tSelection('keepOriginalDates')}
                </button>
                <button
                  onClick={handleConflictRemoveUnavailable}
                  className="flex-1 py-2.5 px-4 text-sm font-semibold bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors cursor-pointer"
                >
                  {dateConflict.unavailableSlugs.length === selectedItems.length
                    ? tSelection('clearSelection')
                    : tSelection('removeUnavailable')}
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
